import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    CarouselOverlayStyleConstantType,
    CarouselBarStyleConstantType,
    CarouselDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getHitArea
} from "../../helpers/theme/control";

export const CAROUSEL_EDGE_RESISTANCE = 0.35;

export const CAROUSEL_FLING_VELOCITY = 0.3;

export const CAROUSEL_SPRING_VELOCITY_SCALE = 1000;

const stylesheet = StyleSheet.create({
    growStart: {
        justifyContent: "flex-start"
    },
    overlay: {
        pointerEvents: "box-none",
        position: "absolute"
    },
    containerVertical: {
        justifyContent: "center",
        flexDirection: "row"
    },
    viewport: {
        alignItems: "flex-start",
        position: "relative",
        overflow: "hidden",
        minWidth: 0,
        flex: 1
    },
    overlayStart: {
        justifyContent: "center",
        bottom: 0,
        top: 0
    },
    outsideSlot: {
        justifyContent: "center",
        alignItems: "center"
    },
    grow: {
        justifyContent: "center",
        flexDirection: "row",
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    },
    growVertical: {
        justifyContent: "center",
        flexDirection: "column"
    },
    mainVertical: {
        flexDirection: "column",
        alignItems: "stretch",
        flexGrow: 1
    },
    barVertical: {
        flexDirection: "column",
        alignItems: "center"
    },
    navVertical: {
        flexDirection: "column"
    },
    viewportVertical: {
        alignItems: "stretch",
        flex: 0,
        flexBasis: "auto",
        flexShrink: 0,
        flexGrow: 0
    },
    main: {
        alignItems: "center",
        flexDirection: "row",
        minWidth: 0
    },
    inert: {
        pointerEvents: "none"
    },
    overlayTop: {
        alignItems: "center",
        start: 0,
        end: 0
    },
    overlayBottom: {
        alignItems: "center",
        start: 0,
        end: 0
    },
    bar: {
        alignItems: "center",
        flexDirection: "row",
        minWidth: 0
    },
    nav: {
        alignItems: "center",
        flexDirection: "row",
        flexShrink: 0
    },
    container: {
        minWidth: 0
    }
});

export const getCarouselOverlayStyle = ({
    shapeConfigs,
    orientation,
    spaces
}: CarouselOverlayStyleConstantType) => {
    const isVertical = orientation === "vertical";

    const zIndex = shapeConfigs.layers.raised;

    return {
        badge: {
            bottom: spaces.spacingSm,
            end: spaces.spacingSm,
            zIndex
        } as Mutable<ViewStyle>,
        next: (isVertical ? {
            bottom: spaces.spacingSm,
            zIndex
        } : {
            end: spaces.spacingSm,
            zIndex
        }) as Mutable<ViewStyle>,
        pause: {
            bottom: spaces.spacingSm,
            start: spaces.spacingSm,
            zIndex
        } as Mutable<ViewStyle>,
        previous: (isVertical ? {
            top: spaces.spacingSm,
            zIndex
        } : {
            start: spaces.spacingSm,
            zIndex
        }) as Mutable<ViewStyle>
    };
};

export const getCarouselBarStyle = ({
    isEndPlacement,
    touchTargets,
    pointerType,
    borders
}: CarouselBarStyleConstantType): Mutable<ViewStyle> => {
    if(!isEndPlacement) {
        return {};
    }

    const hitArea = getHitArea({
        visualSize: borders.indicator * 2,
        touchTargets,
        pointerType
    });

    return {
        marginStart: (borders.indicator * 2 - hitArea.minWidth) / 2
    };
};

export const useStyles = ({
    orientation,
    spaces,
    length
}: Pick<CarouselDynamicStyleType, "orientation" | "spaces" | "length">) => {
    const isVertical = orientation === "vertical";

    const styles = {
        container: {
            gap: isVertical ? spaces.spacingMd : spaces.spacingSm
        } as Mutable<ViewStyle>,
        viewport: {} as Mutable<ViewStyle>,
        main: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        bar: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(isVertical) {
        styles.viewport.height = length;
    }

    return styles;
};
export default stylesheet;
